import { useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_BYTES, uploadImage } from '../../lib/projects';
import { Button } from '../ui';

/**
 * Uploads the file straight to S3 and hands the caller back a pending key to
 * save with the project. Nothing is committed until the form is saved -- an
 * abandoned form leaves an object that the bucket's lifecycle rule expires.
 */
export function ImageUpload({
  previewUrl,
  onUploaded,
  onCleared,
}: {
  previewUrl: string | null;
  onUploaded: (key: string, localPreviewUrl: string) => void;
  onCleared: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);

    // Checked here for a fast failure, and again on the server, which is the
    // authority. The size is also signed into the upload URL, so S3 enforces it.
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError('Choose a JPEG, PNG, WebP, or GIF image.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError(`That image is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is 10 MB.`);
      return;
    }

    setUploading(true);
    try {
      const key = await uploadImage(file);
      onUploaded(key, URL.createObjectURL(file));
    } catch (uploadError: unknown) {
      setError(
        uploadError instanceof ApiError || uploadError instanceof Error
          ? uploadError.message
          : 'The image could not be uploaded.',
      );
    } finally {
      setUploading(false);
      // Allow re-picking the same file after a failure.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div>
      <div className="flex items-center gap-4">
        <div className="flex h-24 w-32 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-stone-200 bg-stone-50">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon />
          )}
        </div>

        <div className="flex flex-col items-start gap-1.5">
          <div className="flex gap-2">
            <Button disabled={uploading} onClick={() => inputRef.current?.click()}>
              {uploading ? 'Uploading…' : previewUrl ? 'Replace' : 'Choose image'}
            </Button>
            {previewUrl ? (
              <Button
                variant="ghost"
                disabled={uploading}
                onClick={() => {
                  setError(null);
                  onCleared();
                }}
              >
                Remove
              </Button>
            ) : null}
          </div>
          <p className="text-sm text-stone-500">JPEG, PNG, WebP, or GIF, up to 10 MB.</p>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />

      {error ? (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ImageIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="h-7 w-7 text-stone-300"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="m21 16-4.5-4.5L7 21" />
    </svg>
  );
}
